import * as React from "react"
import Link from "next/link"
import { ChevronRight } from "lucide-react"
import { BrandLogo } from "./brand-logo"
import { TierPill } from "./tier-pill"
import { formatPrice } from "@/lib/format-utils"
import type { IndexBrand } from "./types"

interface BrandCardListProps {
  brand: IndexBrand
}

/**
 * BrandCard — list-view density (handoff §6).
 *
 * Layout: wide logo tile · body (name + tier pill, meta row, types line) ·
 * right chevron. Whole row is a single `<a>`; hover lifts -2px with orange
 * border. FRIDGE-1495: the row is a size container, measured on its content
 * box. The logo tile is 84×52, and 108×52 from 340 px (the 3-column grid at
 * 1280, phones from 430); the chevron shows only from 400 px, so the text
 * keeps the room it had beside the old 52 px square.
 * FRIDGE-1495 (10/17/2026): a wordmark the tile would paint under 14 px tall
 * takes a 36 px row across the card instead (logo-shape.ts), and the row
 * wraps: the text and chevron go to the line below, with the card's full
 * width. A tile card never wraps (its text column shrinks to fit), and
 * `content-center` keeps either card's content centred in a taller grid row.
 * Used in a 3-col responsive grid (1-col ≤640px, 2-col ≤1024px, 3-col +lg).
 *
 * Note: meta row uses canonical `formatPrice` (not bare `toLocaleString`) so
 * trailing-zero cents render correctly per `Mistake #7` history.
 */
/** FRIDGE-1502 (N1): memoized, so a sort or tier change re-renders only the cards it adds. */
export const BrandCardList = React.memo(function BrandCardList({ brand }: BrandCardListProps) {
  // Meta line shows price range only — the per-brand total product count was
  // intentionally removed to keep the surface focused on shopping signals
  // (price tier, types) rather than catalog-coverage numbers.
  const meta: string[] = []
  if (brand.minPrice != null && brand.maxPrice != null) {
    meta.push(`${formatPrice(brand.minPrice, true)} – ${formatPrice(brand.maxPrice, true)}`)
  }

  const typesSlice = brand.types.slice(0, 3).map(t => t.replace(/-/g, " "))
  const typesMore = brand.types.length - typesSlice.length

  return (
    <Link
      href={`/brands/${brand.slug}`}
      prefetch={false}
      className="group @container flex flex-wrap content-center items-center gap-3.5 rounded-[10px] border border-gray-200 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#ff9951] hover:shadow-[0_8px_20px_-8px_rgba(0,0,0,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9951] focus-visible:ring-offset-2"
    >
      <BrandLogo name={brand.name} slug={brand.slug} logoPath={brand.logo_path} aspect={brand.logoAspect} wide />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-[15px] font-extrabold leading-tight tracking-[-0.01em] text-gray-900">
            {brand.name}
          </h3>
          <TierPill tier={brand.tier} />
        </div>
        <div className="mt-1 truncate text-[12.5px] text-gray-500">
          {meta.join(" · ")}
        </div>
        {typesSlice.length > 0 && (
          <div className="mt-1 truncate text-[12px] capitalize text-gray-500">
            {typesSlice.join(", ")}
            {typesMore > 0 && (
              <span className="ml-1 text-gray-500">+{typesMore} more</span>
            )}
          </div>
        )}
      </div>
      <ChevronRight
        className="ml-auto hidden h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover:text-gray-700 @min-[400px]:block"
        aria-hidden="true"
      />
    </Link>
  )
})
